import React, { useState } from 'react';
import * as yup from 'yup';
import ImageUpload from '../../forms/ImageUpload';
import FormField from '../../forms/FormField/FormField';
import FormContainer, { styles } from '../../forms/FormContainer/FormContainer';
import SubmitButton from '../../forms/SubmitButton/SubmitButton';
import { useFormik } from 'formik';
import submitForm from '../../../services/submitProductUpload';

interface formData {
    price: string[];
    country?: string[];
    certification?: string[];
    packaging?: string[];
    labels?: string[];
    category?: string[];
}
declare const formData: formData;

const validationSchema = yup.object({
    title: yup.string().required('Listing title is required.'),
    description: yup.string().required('Description is required.'),
    price: yup.number().typeError('Price must only contain numbers and no other characters').min(0.0001, 'Price must be higher.').required('Price is required.'),
    qty: yup.number().typeError('Total quantity must only contain numbers and no other characters').min(yup.ref('max_qty'), 'Total quantity must be equal to or higher than maximum quantity.').required('Total quantity is required.'),
    min_qty: yup.number().typeError('Minimum quantity must only contain numbers and no other characters').min(0, 'Minimum quantity must be higher.').max(yup.ref('max_qty'), 'Minimum quantity must be lower or equal to maximum quantity.').max(yup.ref('qty'), 'Minimum quantity must be lower or equal to total quantity.').required('Minimum quantity is required.'),
    max_qty: yup.number().typeError('Maximum quantity must only contain numbers and no other characters').min(yup.ref('min_qty'), 'Maximum quantity must be higher than or equal to minimum quantity.').max(yup.ref('qty'), 'Maximum quantity must be lower or equal to total quantity.').required('Maximum quantity is required.'),
    priceType: yup.string().required('Price type is required.'),
    category: yup.string().required('Category is required.'),
    certification: yup.array(),
    country: yup.string().required('Country of origin is required.'),
    postage: yup.string().required('Postage type is required.'),
    preservation: yup.string().required('Preservation type is required.'),
    packaging: yup.array(),
    labels: yup.array(),
    images: yup.array()
        .min(1, 'Please upload an image.')
        .test('fileSize', 'Image must be less than 8MB.', value => {
            let valid = true;
            value?.forEach(file => {
                if (file.size > 8 * 1024 * 1024) {
                    valid = false;
                }
            });
            return valid;
        })
        .test('fileType', 'Unsupported file type, only JPG, PNG and GIF are accepted.', value => {
            const supportedTypes = ['image/png', 'image/jpeg', 'image/gif'];
            let valid = true;
            value?.forEach(file => {
                if (!supportedTypes.includes(file.type)) {
                    valid = false;
                }
            });
            return valid;
        }),
});

export default function Form({ setJourney, setProfileUrl }) {
    const [loading, setLoading] = useState(false);
    const [packagingOptions, setPackagingOptions] = useState(formData.packaging || []);
    const [customPackagingInput, setCustomPackagingInput] = useState('');

    const addCustomPackaging = () => {
        const trimmedInput = customPackagingInput.trim();
        if (trimmedInput) {
            const lowerInput = trimmedInput.toLowerCase();
            const lowerKeys = packagingOptions.map(option => option.toLowerCase());
            if (!lowerKeys.includes(lowerInput)) {
                setPackagingOptions([...packagingOptions, trimmedInput]);
                // Pre-tick the new packaging option
                formik.setFieldValue('packaging', [...formik.values.packaging, trimmedInput]);
            }
            setCustomPackagingInput('');
        }
    };

    const formik = useFormik({
        initialValues: {
            title: '',
            description: '',
            price: '',
            qty: 1,
            min_qty: 1,
            max_qty: 1,
            priceType: 'kg',
            category: '',
            country: 'gb-nir',
            certification: [],
            postage: '',
            preservation: '',
            packaging: [],
            labels: [],
            images: [],
        },
        validationSchema: validationSchema,
        onSubmit: values => {
            // Filter out blank packaging entries before submission
            const filteredValues = {
                ...values,
                packaging: values.packaging.filter(pkg => pkg.trim() !== ''),
            };
            submitForm(filteredValues, setJourney, setLoading, setProfileUrl);
        },
    });

    return (
        <FormContainer>
            <form className={loading ? styles.loading : ""} onSubmit={formik.handleSubmit}>
                <FormField label="Listing title:" type="text" name="title" formik={formik} />
                <FormField label="Description:" type="textarea" name="description" formik={formik} />
                <FormField label="Price (£):" type="text" name="price" formik={formik} />
                <FormField label="Price type:" type="radio" name="priceType" formik={formik} options={formData.price} />
                <FormField label={`Quantity available (${formik.values.priceType}):`} type="text" name="qty" formik={formik} />
                <FormField label={`Minimum order quantity (${formik.values.priceType}):`} type="text" name="min_qty" formik={formik} />
                <FormField label={`Maximum order quantity (${formik.values.priceType}):`} type="text" name="max_qty" formik={formik} />
                <FormField label="Category:" type="select" name="category" formik={formik} options={formData.category} />
                <FormField label="Country of origin:" type="select" name="country" formik={formik} options={formData.country} />
                <FormField label="Certification:" type="checkbox" name="certification" formik={formik} options={formData.certification} />
                <FormField label="Postage:" type="radio" name="postage" formik={formik} options={['Collection', 'Delivery']} />
                <FormField label="Preservation:" type="radio" name="preservation" formik={formik} options={['Fresh', 'Frozen']} />
                <FormField label="Packaging:" type="checkbox" name="packaging" formik={formik} options={packagingOptions} />
                <div className="meatex-form-row">
                    <label>Additional packaging information:</label>
                    <div className="meatex-form-row__addition">
                        <input
                            type="text"
                            value={customPackagingInput}
                            onChange={(e) => setCustomPackagingInput(e.target.value)}
                            placeholder="Enter custom packaging information"
                        />
                        <button className="meatex-button" type="button" onClick={addCustomPackaging}>
                            Add
                        </button>
                    </div>
                </div>
                <FormField label="Labels:" type="checkbox" name="labels" formik={formik} options={formData.labels} />
                <ImageUpload formik={formik} label="Upload product images" dropzoneText="Drop your images here or click to select." fieldName="images" />
                <SubmitButton formik={formik} text="Upload product" />
            </form>
        </FormContainer>
    );
}